import Button from '@/dashboard/components/Button';
import Spinner from '@/dashboard/components/Spinner';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@/dashboard/hooks/queries/useSellerQueries';
import { adminSellerKey } from '@/dashboard/lib/queryKeys';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';

/**
 * We want to show a dialog of the current ghosting status
 * 1. the admin is currently ghosting the seller: isAdminLoggedInAsSeller
 *    - show a dialog with the following:
 *      - Logged in as Seller X
 *      - Log out of seller
 *      - Open in Admin
 * 2. the admin is not ghosting the seller: !isAdminLoggedInAsSeller
 *    - show a dialog with the following:
 *      - Not logged in as Seller X
 *      - Log in as Seller X
 *      - Open in admin
 */
export default function AdminGhostingDialog({ sellerId }) {
  const { isAdminLoggedInAsSeller, logAdminInAsSeller, userSellerId, seller, isAdmin } = useAuth();
  const { getSeller } = useSellerQueries();

  // if this is not an admin, don't show the dialog
  if (!isAdmin) {
    return null;
  }

  // if the admin is not ghosting the seller and there is no sellerId, don't show the dialog
  if (!isAdminLoggedInAsSeller && !sellerId) {
    return null;
  }

  // if the admin is viewing their own account, don't show the dialog
  if (!isAdminLoggedInAsSeller && userSellerId === sellerId) {
    return null;
  }

  // if the admin is not ghosting the seller, fetch the seller data
  const { data: sellerData, isLoading } = useQuery({
    queryKey: adminSellerKey(sellerId),
    queryFn: () => getSeller(sellerId),
    enabled: sellerId && !isAdminLoggedInAsSeller,
  });

  const handleLogout = useCallback(() => {
    logAdminInAsSeller(userSellerId);
  }, [logAdminInAsSeller, userSellerId]);

  // if the admin is ghosting the seller, show the dialog with seller data from useAuth
  if (isAdminLoggedInAsSeller) {
    const sellerName = seller?.displayName || seller?.name;

    return (
      <FloatingDialog>
        <span className="text-sm font-bold">Logged in:</span>
        <h3 className="mb-2">
          <span className="text-chocolate-1000">{sellerName}</span>
        </h3>
        <div className="flex gap-x-4">
          <Button appearance="secondary" onClick={handleLogout} size="small">
            Log out
          </Button>

          <Button appearance="secondary" href={`/admin/merchants/${sellerId}`} size="small">
            Open in admin
          </Button>
        </div>
      </FloatingDialog>
    );
  }

  // if the seller data is loading, show a spinner
  if (isLoading) {
    return (
      <FloatingDialog>
        <Spinner />
      </FloatingDialog>
    );
  }

  // if the seller data is loaded, show the dialog with seller data from the query
  const sellerName = sellerData?.displayName || sellerData?.name;
  return (
    <FloatingDialog>
      <span className="text-sm font-bold">Viewing:</span>
      <h3 className="mb-2">
        <span className="text-chocolate-1000">{sellerName}</span>
      </h3>
      <div className="gap-x-4 flex">
        <Button appearance="primary" onClick={() => logAdminInAsSeller(sellerId)} size="small">
          Log in
        </Button>

        <Button appearance="secondary" href={`/admin/merchants/${sellerId}`} size="small">
          Open in admin
        </Button>
      </div>
    </FloatingDialog>
  );
}

function FloatingDialog({ children }) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div
      className={clsx(
        'fixed max-w-lg bottom-4 left-0 right-0 mx-auto flex items-center justify-center z-50',
        {
          hidden: !isOpen,
        },
      )}
    >
      <div className="max-w-lg bg-white border border-chocolate-1000 p-3 rounded-lg shadow-lg relative">
        {children}

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="absolute -top-2.5 -right-2.5 bg-white rounded-full p-1 shadow-md hover:shadow-sm hover:bg-gray-50 transition-all duration-200"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
